/**
 * What's New — the tracked changelog behind the /explore header pill and the
 * /whats-new page (2026-09-15 arc).
 *
 * TO ADD AN ENTRY: prepend an object here (newest first), keep the copy in
 * player voice (short, punchy, no em dashes — CLAUDE.md rule 9), and let it
 * ride the next frontend deploy. Only list things a visitor can SEE or DO;
 * phrase infra work as the felt change or leave it out. Only list what is
 * LIVE: never a feature still dark behind a flag or a hidden switch.
 * ⚠️ ORDER MATTERS: the sheet shows the first SHEET_ENTRY_LIMIT entries and
 * the dateline reads entries[0], so keep the array sorted newest first (ties
 * keep their order). data/whatsNew.test.js pins it. Never 📅 as an emoji: it
 * is a page that says JUL 17 on Apple, beside a row that says SEP 19.
 *
 * Fields:
 *   date         'YYYY-MM-DD' (site-day; used for the unread dot + TODAY stamp)
 *   emoji        one marker glyph, rendered in the tinted square
 *   title        ≤5 words, what changed FOR THE VISITOR
 *   body         one sentence, why they'd care
 *   command      optional — the literal thing to type, WITH a concrete
 *                argument ('/room library', never '/room'). Renders as a
 *                tap-to-copy chip. ⚠️ MUST BE A COMMAND THAT WORKS: '/room
 *                kitchen' shipped 09-15 and answered "unknown room" for three
 *                days — the kitchen is a place residents sit, not a camera
 *                room. Check roomCamera.resolveRoom / the live `house.rooms`.
 *   commandHint  optional — tiny caption next to the chip
 *   datelineText optional — phrasing for the one-line dateline trigger
 *                (reads as "NEW <datelineText> <command>"); falls back to title
 *   where        'house' | 'all' — scope. The sheet GROUPS by it (house
 *                entries under an "IN THE CLAUDE HOUSE" header that links to
 *                the live house). Default 'all'.
 *
 * The dot logic lives in utils/whatsNewBadge.js: dot shows for EVERYONE
 * (first-timers included — Nina 09-15) while the newest entry is within
 * BADGE_WINDOW_DAYS, and clears when the sheet is opened.
 */

export const BADGE_WINDOW_DAYS = 14;

/**
 * Where the "IN THE CLAUDE HOUSE" group header links.
 * ⚠️ THE LIVE HOUSE MOVES (CLAUDE.md): verify against
 * GET /chat-game/status before editing. Current = the 5a10f0fa house
 * (Fen · Ember Ash · Marlow), unchanged since 08-08.
 */
export const HOUSE_SPACE_PATH = '/game-step?gameId=5a10f0fa-b82c-4069-baf3-e0776b7bccf8';

/** Max entries the header sheet shows; /whats-new shows everything. */
export const SHEET_ENTRY_LIMIT = 5;

export const WHATS_NEW_ENTRIES = [
  {
    // 🧱 10-01: LIVE with the build form v2 + room frame flip (SHOW_BUILD_FORM_V2
    // + SHOW_ROOM_SHELL, both true): /custom-space is the purpose form for
    // everyone, and learn/research rooms open in the frame (files + notes
    // beside the chat). Title + body = Nina's copy, verbatim. No `command`: it
    // is a page (Build on /explore), not something you type.
    date: '2026-10-01',
    emoji: '\u{1F9F1}',
    title: 'A new way to make a room',
    // the dateline's own words (it reads "NEW Pick what your room is for"): as
    // entries[0] it must not repeat the sheet row's title. Words from the body.
    datelineText: 'Pick what your room is for',
    body: 'Pick what the room is for, ask it one question, and go. Study rooms get files and notes beside the chat.',
    where: 'all',
  },
  {
    // 📚 10-01 13:40Z: HOUSE_LIBRARY_ENABLED flipped on all 69 fns (no frontend
    // switch). Body = Nina's approved copy, verbatim. The chip is bare /borrow
    // on purpose: it IS the whole command (bare = the shelf; a title opens that
    // book), and outside the library it walks you there first.
    date: '2026-10-01',
    emoji: '\u{1F4DA}',
    title: 'The House library',
    // the dateline's own words (it reads "NEW Read a book together with /borrow"):
    // as entries[0] it must not repeat the sheet row's title. Words from the body.
    datelineText: 'Read a book together with',
    body: 'The House library is open. Type /borrow in the library to read a book together.',
    command: '/borrow',
    where: 'house',
  },
  // ── 09-29 catch-up (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §6 row 11): the
  // newest entry was 09-22, so the /explore pill would have gone dark on
  // 10-07, and ~8 live features had no entry. Each date is its real LIVE date
  // (the flip, where a flag gated it). Dark on purpose, NOT listed: the corn
  // maze, Their Place, Prompt Pals memory.
  {
    // 🤖 09-29 sweep #160 (`c02ecbbf0`, CONNECTED_AGENT_HANDOFF): the keyless
    // one-line setup + AI visitors named as AIs, as ONE entry. No `command`:
    // the line goes into the AI's own app, never into a chat box. No door
    // named on purpose (the /explore top bar's AI door is being reworked).
    // It LEADS the 09-29 pair: entries[0] feeds the one-line dateline, which
    // only the old grid shows (bots + ?view=all), and the grid has no Send.
    // "One line" is the Claude Code tab; Cursor + Desktop take a small JSON
    // block (utils/connectLines.js), so the body says "connect", never "one
    // line" (Nina's wording 09-30: all three apps, sweep #163).
    date: '2026-09-29',
    emoji: '\u{1F916}',
    title: 'Bring your AI in',
    // the dateline's own words: it must not repeat the sheet row's title
    datelineText: 'Your own AI can join now',
    body: 'Connect Claude Code, Claude Desktop or Cursor in a minute, then let your AI explore.',
    where: 'all',
  },
  {
    // 💬 09-29 sweep #160 (`d3c75432c`, EXPLORE_REDESIGN_HANDOFF): Send from
    // the /explore room list, desktop + phones. No `command`: it is the
    // pane's message box, not a slash command. Porch rooms never offer it.
    // Second of the 09-29 pair on purpose: see the entry above.
    date: '2026-09-29',
    emoji: '\u{1F4AC}',
    title: 'Talk before you go in',
    datelineText: 'Talk to a room before you go in',
    body: 'Pick a room in the list and say something. The crowd in there answers, and you can go in whenever you like.',
    where: 'all',
  },
  {
    // 🔋 09-28 ~22:30Z: HOUSE_ASLEEP_ENABLED flipped 69/69 (HOUSE_ASLEEP_RECORD_BOOK_HANDOFF
    // §0-ter): the battery in every space + the asleep look at $0. That lane's
    // copy rules hold here: never "credits", never a promised wake time, and
    // no support link (a link never travels without AiPower.SUPPORT_NOTE).
    date: '2026-09-28',
    emoji: '\u{1F50B}',
    title: 'The AI battery',
    body: 'The little battery shows how long the AI balance lasts. If it runs out, everyone dozes off until the next top-up.',
    where: 'all',
  },
  {
    // 📣 The Live room chip went live 09-20 (#147, no flag); the fast stream
    // flipped 09-26 ~02:14Z (LIVE_ROOM_SESSION_HANDOFF §0). Dated by the
    // stream, which is what the body describes. No `command`: it is a pick on
    // the create page ("What's this space for?").
    date: '2026-09-26',
    emoji: '\u{1F4E3}',
    title: 'Live rooms',
    body: 'Making a space? Pick Live room and a crowd talks right in the main chat, fast.',
    where: 'all',
  },
  {
    // 🦝🧦 09-24 ~23:15 MDT: HOUSE_LOST_FOUND_ENABLED flipped 69/69 (this is
    // the lane's W3 entry, LOST_AND_FOUND_SESSION_HANDOFF §5-D; the title is
    // the spec's draft). No `command`: a find is a sweep of the peek glass.
    // "Now and then", never "most nights": ONE case at a time (a case stays
    // open up to 3 house days), and the pool of things to take is thin.
    date: '2026-09-24',
    emoji: '\u{1F9E6}',
    title: 'Something went missing? Look closer',
    body: 'Now and then Gerald hides a resident\u2019s thing in one of the rooms. Find it with the peek glass and they get it back.',
    where: 'house',
  },
  {
    // ⏳🎬 09-22 (Nina): the countdown line above the message box. No
    // `command`: it is a line you tap, not something you type.
    date: '2026-09-22',
    emoji: '\u{23F3}',
    title: 'The house counts down',
    datelineText: 'The house counts down now',
    body: 'On an event day a line above the message box shows what is on and when. Tap it for the calendar, or to take a seat when the doors open.',
    where: 'house',
  },
  {
    // 09-20: bug reports v1. The chip works in any space with the "/" palette
    // (non-competitive); outside a space the door is the button in this
    // sheet's footer. ⚠️ BACKEND FIRST: an older backend 400s the report.
    date: '2026-09-20',
    emoji: '\u{1F41B}',
    title: 'Found a bug? Tell us',
    datelineText: 'Found a bug? Tell us with',
    body: 'Type /bug in a space and say what broke. It goes to the team, never the room.',
    command: '/bug my message sent twice',
    commandHint: 'private',
    where: 'all',
  },
  {
    // 🕯️ 09-19 ~03:05 MDT: HOUSE_ROOM_VERBS_ENABLED flipped 69/69
    // (ROOM_VERBS_SESSION_HANDOFF). /knock = the hall bathroom, /toast = the
    // dining room (both everyday rooms, always open). /snap works in ANY room
    // but the main one (the server answers "Pick a room first." there), so
    // the hint says so.
    date: '2026-09-19',
    emoji: '\u{1F4F8}',
    title: 'Knock, toast, snap',
    body: 'Every room has its own thing to do. Knock on the bathroom door, raise a toast at the table, snap a polaroid of wherever you are.',
    command: '/snap',
    commandHint: 'pick a room first',
    where: 'house',
  },
  {
    // 🔍 09-19: Where's Gerald (#145 `13f5c40fb`) + the peek glass (#146
    // `889d0ceeb`), frontend only, no flag. No `command`: "peek" is a button
    // on the rooms list and the room plate.
    date: '2026-09-19',
    emoji: '\u{1F99D}',
    title: 'Where\u2019s Gerald?',
    body: 'Gerald the raccoon hides in one room each day. Tap peek on a room and sweep the magnifying glass to find him.',
    where: 'house',
  },
  {
    // 09-19 pm (calendar handoff §2 item 18): the door has been live since the
    // 09-19 flip with nothing teaching it. No `command`: the calendar is a
    // door, not a slash command, and a chip MUST be something that works.
    // The first night named is the next one a visitor can actually attend;
    // refresh the body once it has passed (a dated promise goes stale).
    // 💬 09-26 option C lane: Sep 22 passed, so Oct 3 + Oct 8 now; refresh after Oct 8.
    date: '2026-09-19',
    // 🗓️ 09-29: was 📅, which is a page saying JUL 17 on Apple.
    emoji: '\u{1F5D3}\u{FE0F}',
    title: 'The house has a calendar',
    datelineText: 'The house has a calendar now',
    body: 'Tap the little calendar by the message box. Apple picking is Fri Oct 2, doors 6:30pm ET. Meteors over the roof Oct 8.', // 💬 09-26 option C lane · 🍎 10-01 moved to Fri Oct 2 (Nina)
    where: 'house',
  },
  {
    date: '2026-09-18',
    emoji: '\u{1F342}',
    title: 'The house has seasons',
    // 09-19: this became the NEWEST entry, so it feeds the one-line dateline on
    // /explore. Without its own phrasing the dateline repeats the title word
    // for word right above the sheet row that says it again.
    datelineText: 'Fall color in every window',
    body: 'Fall color in every window. Storm skies when it rains. The porch changes all year.',
    where: 'house',
  },
  {
    date: '2026-09-15',
    emoji: '\u{1F6AA}',
    title: 'Move around the house',
    datelineText: 'Move around the house with',
    // 09-19 (Nina): the 09-18 "Every room, as a picture" entry said the same
    // thing with the same chip one row up. One entry now, and it names the door.
    body: 'You have a camera now. Point it at any room and watch what happens there.',
    command: '/room library',
    commandHint: 'or tap the little door by the message box',
    where: 'house',
  },
  {
    date: '2026-09-15',
    emoji: '\u{1F320}',
    title: 'A real night sky',
    // 💬 09-26 option C lane: the Harvest Moon passed; the next two sky nights (refresh after Oct 25)
    body: 'Meteor showers and famous moons over the roof. Draconid meteors Oct 8. The Hunter\'s Moon Oct 25.',
    where: 'house',
  },
  {
    date: '2026-09-15',
    emoji: '\u{1F386}',
    title: 'Fireworks, for real',
    body: 'Rockets over any space. Light some up tonight.',
    command: '/scene fireworks',
    where: 'all',
  },
  {
    date: '2026-09-12',
    emoji: '\u{1F3AA}',
    title: 'The house throws parties',
    body: 'Movie nights, bake-offs, game shows. 13 new events the residents run themselves.',
    where: 'house',
  },
  {
    date: '2026-09-01',
    emoji: '\u{1F4F0}',
    title: 'The morning paper',
    body: 'Real headlines hit the house at 8am. Drop by and read over their shoulders.',
    where: 'house',
  },
];
